import type { CSSObject } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { varAlpha } from 'minimal-shared/utils';

import { snackbarClasses } from './classes';

// ----------------------------------------------------------------------

export const SnackbarWrapper = styled('div')(({ theme }) => {
  const baseStyles: Record<string, CSSObject> = {
    toastDefault: {
      padding: theme.spacing(1, 1, 1, 1.5),
      boxShadow: theme.vars.customShadows.z8,
      color: theme.vars.palette.background.paper,
      backgroundColor: theme.vars.palette.text.primary,
    },
    toastColor: {
      padding: theme.spacing(0.5, 1, 0.5, 0.5),
      boxShadow: theme.vars.customShadows.z8,
      color: theme.vars.palette.text.primary,
      backgroundColor: theme.vars.palette.background.paper,
      border: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
    },
    toastLoader: {
      padding: theme.spacing(0.5, 1, 0.5, 0.5),
      boxShadow: theme.vars.customShadows.z8,
      color: theme.vars.palette.text.primary,
      backgroundColor: theme.vars.palette.background.paper,
      border: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
    },
  };

  const loadingStyles: CSSObject = {
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'none',
    transform: 'none',
    overflow: 'hidden',
    alignItems: 'center',
    position: 'relative',
    borderRadius: 'inherit',
    justifyContent: 'center',
    background: theme.vars.palette.background.neutral,
    [`& .${snackbarClasses.loadingIcon}`]: {
      zIndex: 9,
      width: 24,
      height: 24,
      borderRadius: '50%',
      animation: 'rotate 3s infinite linear',
      background: `conic-gradient(transparent, ${varAlpha(theme.vars.palette.text.disabledChannel, 0.64)})`,
    },
    [snackbarClasses.loaderVisible]: { display: 'flex' },
  };

  return {
    width: 300,
    [`& .${snackbarClasses.toast}`]: {
      gap: 12,
      width: '100%',
      minHeight: 52,
      display: 'flex',
      borderRadius: 12,
      alignItems: 'center',
    },
    /**
     * Content
     */
    [`& .${snackbarClasses.content}`]: { gap: 0, flex: '1 1 auto' },
    [`& .${snackbarClasses.title}`]: { fontSize: theme.typography.subtitle2.fontSize },
    [`& .${snackbarClasses.description}`]: { ...theme.typography.caption, opacity: 0.64 },
    // /**
    //  * Buttons
    //  */
    // Sonner paints the action button in its own inverted palette, which lands it light-on-light: the default toast is
    // itself inverted (white in dark mode), so "Undo" all but disappeared. Re-inverting to the page's own colours puts
    // a solid button back on the toast in either theme. `!important` because sonner's own rule is more specific.
    [`& .${snackbarClasses.actionButton}`]: {
      color: `${theme.vars.palette.text.primary} !important`,
      background: `${theme.vars.palette.background.paper} !important`,
      border: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.16)} !important`,
      transition: theme.transitions.create(['opacity']),
      '&:hover': { opacity: 0.88 },
    },
    [`& .${snackbarClasses.cancelButton}`]: {},
    [`& .${snackbarClasses.closeButton}`]: {
      top: 0,
      right: 0,
      left: 'auto',
      color: theme.vars.palette.text.secondary,
      background: `${theme.vars.palette.background.paper} !important`,
      transform: 'translate(-6px, 6px)',
      borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.16),
      transition: theme.transitions.create(['background-color', 'border-color']),
      '&:hover': {
        borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.24),
        background: `${varAlpha(theme.vars.palette.grey['500Channel'], 0.08)} !important`,
      },
    },
    /**
     * Icon
     */
    [`.${snackbarClasses.icon}`]: {
      margin: 0,
      width: 24,
      height: 24,
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'flex-start',
      marginRight: 4,
      marginTop: 2,
      [`& .${snackbarClasses.iconSvg}`]: { width: 22, height: 22, fontSize: 0 },
    },

    '@keyframes rotate': { to: { transform: 'rotate(1turn)' } },

    /**
     * @variant default
     */
    [`& .${snackbarClasses.default}`]: {
      ...baseStyles.toastDefault,
      [`&:has(${snackbarClasses.closeBtnVisible})`]: {
        [`& .${snackbarClasses.content}`]: { paddingRight: 32 },
      },
      [`&:has(.${snackbarClasses.loader})`]: baseStyles.toastLoader,
      /**
       * @with loader
       */
      [`&:has(.${snackbarClasses.loader})`]: baseStyles.toastLoader,
      [`& .${snackbarClasses.loader}`]: loadingStyles,
    },
    /**
     * @variant error
     */
    [`& .${snackbarClasses.error}`]: {
      ...baseStyles.toastColor,
      '&[data-styled]': { background: theme.vars.palette.background.paper },
      // Red, so a failure is told apart from a success at a glance; the icon shapes alone were too alike.
      [`& .${snackbarClasses.icon}`]: { color: theme.vars.palette.error.main },
    },
    /**
     * @variant success
     */
    [`& .${snackbarClasses.success}`]: {
      ...baseStyles.toastColor,
      '&[data-styled]': { background: theme.vars.palette.background.paper },
      [`& .${snackbarClasses.icon}`]: { color: theme.vars.palette.text.primary },
    },
    /**
     * @variant warning
     */
    [`& .${snackbarClasses.warning}`]: {
      ...baseStyles.toastColor,
      '&[data-styled]': { background: theme.vars.palette.background.paper },
      [`& .${snackbarClasses.icon}`]: { color: theme.vars.palette.text.primary },
    },
    /**
     * @variant info
     */
    [`& .${snackbarClasses.info}`]: {
      ...baseStyles.toastColor,
      '&[data-styled]': { background: theme.vars.palette.background.paper },
      [`& .${snackbarClasses.icon}`]: { color: theme.vars.palette.text.primary },
    },
  };
});
